import {
  AccountIcon,
  ActivityIcon,
  AssistantIcon,
  AudienceIcon,
  type ColumnDef,
  ContactIcon,
  ContractIcon,
  DashboardIcon,
  EquipmentIcon,
  InvoiceIcon,
  DocumentIcon,
  LeadIcon,
  MeetingIcon,
  OpportunityIcon,
  PartIcon,
  ProjectIcon,
  QueueIcon,
  ReportIcon,
  ScheduleIcon,
  TechnicianIcon,
  TimeEntryIcon,
  labelFromKey,
} from '@olytic/foundation';
import type { ReactNode } from 'react';

import { type ObjectKey, OBJECTS } from '../navigation';
import {
  formatBytes,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  humanize,
} from './format';
import type { MetaFieldType, ObjectMeta, ObjectRow } from './objectsApi';
import { isObjectKey, recordPath } from './routes';

/**
 * One definition per ops object: its list columns, filters and fields. Its
 * list views are rows on the server (migration 0050, listViewsApi.ts).
 * A list page is just `<ObjectListPage def={OBJECT_DEFS.accounts} />`. Column
 * and filter keys are the API's column names, so a sortable column's key is
 * what `sort=` sends and a filter field's key is what `filters=` names.
 */

/**
 * A field offered in the filter builder. `select` is a picklist: its values
 * are `options` when the schema fixes them, otherwise read from
 * GET /api/objects/:object/meta. Picklists are also what a board (Kanban)
 * groups by, so `picklistKeys(def)` lists the fields a board could use.
 */
export interface ObjectFilterField {
  key: string;
  label: string;
  kind: 'text' | 'number' | 'date' | 'select' | 'boolean';
  options?: string[];
}

/** A field as the API returns it, for Setup → Objects. Types are plain language. */
export interface ObjectField {
  name: string;
  type: string;
  references?: ObjectKey;
}

/**
 * A list column of an object described by the schema: only what its type in
 * /meta does not say. `format: 'raw'` shows the value as it comes.
 */
export interface SchemaColumn {
  key: string;
  /** A sort key other than the column's own (a contact's Name sorts by last name). */
  sortable?: string;
  /** A value made from other fields (a contact's first and last name), for a column the list does not return. */
  get?: (row: ObjectRow) => unknown;
  primary?: boolean;
  width?: number;
  hidden?: boolean;
  nowrap?: boolean;
  /** Tabular figures, for numbers the schema keeps as text (a lead's Urgency). */
  figures?: boolean;
  /** Left off the list on a phone (a document's File Type). */
  hideOnMobile?: boolean;
  mobileHeader?: boolean;
  format?: Format | 'raw';
}

/**
 * The list of an object whose fields come from the database (the server's
 * SCHEMA_OBJECTS): its default columns and its first filters, in order, with
 * their overrides. Labels come from the record page; types, picklist values
 * and lookups from /meta (listState.ts resolveDef). Every other column and
 * field of the table is offered too, so a new column needs no code here.
 */
export interface SchemaList {
  columns: SchemaColumn[];
  filters: string[];
  /** List labels that differ from the record page's (a lead's created_at is Submitted there, Created here). */
  labels?: Record<string, string>;
  /** Filter labels that differ from the list's (a document's Size is filtered in bytes: Size (Bytes)). */
  filterLabels?: Record<string, string>;
}

export interface ObjectDef {
  key: ObjectKey;
  label: string;
  /** Lower-case plural for prose, e.g. "time entries". */
  noun: string;
  icon: ReactNode;
  description: string;
  columns: ColumnDef<ObjectRow>[];
  /** Fields offered in the filter builder. */
  filterFields: ObjectFilterField[];
  /** The picklist a board view groups by by default, for when Kanban is turned on. */
  boardField?: string;
  /** Column keys a Kanban card shows under the name (at most 3); omitted, the first three columns. */
  kanbanCardFields?: string[];
  /** A numeric column summed in each Kanban column's header, and how to show the sum. */
  kanbanSum?: { key: string; format: (total: number) => string };
  /** Setup → Objects' field list; an object described by the schema reads it from /meta instead. */
  fields: ObjectField[];
  /** Set for an object described by the schema: `columns` and `filterFields` are then made from /meta. */
  schema?: SchemaList;
}

/** The keys of an object's picklist fields: the fields a board view can group by. */
export function picklistKeys(def: ObjectDef): string[] {
  return def.filterFields.filter((field) => field.kind === 'select').map((field) => field.key);
}

type Col = ColumnDef<ObjectRow>;
type Format = (value: unknown) => string;

/**
 * An object described by the schema: its list as `schema`, and the columns
 * shown until /meta answers (labelled from their keys, values as they come).
 */
function schemaList(list: SchemaList): Pick<ObjectDef, 'columns' | 'filterFields' | 'fields' | 'schema'> {
  return {
    columns: list.columns.map(({ format: _format, ...column }) => ({ ...column, label: list.labels?.[column.key] ?? labelFromKey(column.key), sortable: column.sortable ?? true })),
    filterFields: [],
    fields: [],
    schema: list,
  };
}

/** A sortable column showing one API field, optionally formatted. */
function col(key: string, label: string, format?: Format, extra: Partial<Col> = {}): Col {
  return {
    key,
    label,
    sortable: true,
    // A name column keeps the library's link styling only without a custom render, so it formats through `get`.
    get: format && extra.primary ? (row) => format(row[key]) : (row) => row[key] ?? '',
    ...(format && !extra.primary ? { render: (row: ObjectRow) => format(row[key]) } : {}),
    ...extra,
  };
}

const str = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

/** A lead's record name: first and last name, else the email. */
export const leadName = (row: Record<string, unknown>): string =>
  `${str(row.first_name)} ${str(row.last_name)}`.trim() || str(row.email);

const nameCol = (label: string): Col => col('name', label, undefined, { primary: true, width: 240 });
/** A lookup column: shows the parent's name and links to its record page. */
const lookup = (object: ObjectKey, idKey: string) => ({ object, idKey });
// Numbers sit left, under their headers, in tabular figures.
const figures: Partial<Col> = { figures: true, nowrap: true };
const date: Partial<Col> = { nowrap: true };
/** A record's Owner: a person, edited in place like any lookup. */
const ownerCol: Partial<Col> = { ...date, link: lookup('people', 'owner_id') };

const T = {
  id: 'ID',
  text: 'Text',
  email: 'Email',
  number: 'Number',
  currency: 'Currency',
  percent: 'Percent',
  bool: 'Yes / No',
  date: 'Date',
  datetime: 'Date & time',
  picklist: 'Picklist',
  lookup: 'Lookup',
} as const;

const META_TYPES: Record<MetaFieldType, string> = {
  text: T.text, number: T.number, currency: T.currency, percent: T.percent,
  boolean: T.bool, date: T.date, datetime: T.datetime, picklist: T.picklist,
};

/**
 * Setup → Objects' fields of an object described by the schema, from /meta:
 * its id, each column by its type, and each lookup as its id (referencing the
 * related object) and its name.
 */
export function schemaObjectFields(meta: Pick<ObjectMeta, 'columns'>): ObjectField[] {
  return [
    f('id', T.id),
    ...(meta.columns ?? []).flatMap((c) =>
      c.lookup && isObjectKey(c.lookup.object)
        ? [f(c.lookup.idKey, T.lookup, c.lookup.object), f(c.key, META_TYPES[c.type])]
        : [f(c.key, META_TYPES[c.type])],
    ),
  ];
}

const f = (name: string, type: string, references?: ObjectKey): ObjectField =>
  references ? { name, type, references } : { name, type };

const label = (key: ObjectKey): string => OBJECTS.find((o) => o.key === key)?.label ?? key;

/** Filter-field helpers; a picklist without options gets its values from /meta. */
const ftext = (key: string, l: string): ObjectFilterField => ({ key, label: l, kind: 'text' });
const fnum = (key: string, l: string): ObjectFilterField => ({ key, label: l, kind: 'number' });
const fdate = (key: string, l: string): ObjectFilterField => ({ key, label: l, kind: 'date' });
const fbool = (key: string, l: string): ObjectFilterField => ({ key, label: l, kind: 'boolean' });
const fpick = (key: string, l: string, options?: string[]): ObjectFilterField =>
  options ? { key, label: l, kind: 'select', options } : { key, label: l, kind: 'select' };

// Picklist values the schema fixes (enums and CHECK constraints).
const OPPORTUNITY_STAGES = ['qualify', 'shape', 'propose', 'negotiate', 'commit', 'closed_won', 'closed_lost'];
const ACTIVITY_CHANNELS = ['email', 'meeting', 'slack', 'linkedin_dm', 'sms', 'call', 'note'];
const ACTIVITY_DIRECTIONS = ['inbound', 'outbound', 'internal'];
const PRODUCT_FAMILIES = ['consulting', 'studio', 'software', 'agents', 'shared'];
const REVENUE_STREAMS = ['workstream', 'consulting', 'studio', 'software', 'agents', 'shared'];
const BILLINGS = ['one_time', 'monthly'];
const AGENT_CONVERSATION_OUTCOMES = ['booking_ready', 'meeting_booked', 'email_captured', 'unqualified', 'abandoned'];
const RESEARCH_GRADES = ['A', 'B', 'C', 'D'];
const PRICE_GRID_PRESETS = ['company_size', 'company_size_and_quantity'];

/** "Jan 2027" for a YYYY-MM-DD date. */
const formatMonth = (value: unknown): string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    : '';
const QUOTE_KINDS = ['new', 'change_order'];
const QUOTE_STATUSES = ['draft', 'presented', 'accepted', 'declined', 'expired'];
const INVOICE_STATUSES = ['draft', 'approved', 'synced', 'paid', 'void'];
const INVOICE_RUN_STATUSES = ['running', 'completed', 'failed'];
const BILLING_SCHEDULE_STATUSES = ['planned', 'ready', 'invoiced', 'cancelled'];
const BILLING_SCHEDULE_SOURCES = ['quote', 'order'];
const BILLING_METHODS = ['milestone', 'installments', 'recurring', 'on_completion', 'on_purchase'];
const BILLING_TIMINGS = ['in_advance', 'in_arrears'];
const BILLING_FREQUENCIES = ['one_time', 'monthly', 'quarterly', 'annual'];
const BILLING_TRIGGERS = ['on_acceptance', 'milestone', 'month_offset', 'completion'];
const GUIDELINE_TOPICS = ['pricing', 'delivery', 'opportunity_strategy', 'document'];
const GUIDELINE_SCOPES = ['global', 'product', 'opportunity', 'deliverable_type'];
const DELIVERABLE_STATUSES = ['draft', 'sent', 'signed', 'superseded'];
const DELIVERABLE_SOURCES = ['quote', 'project'];
const GUIDE_STATUSES = ['draft', 'live', 'retired'];
const GUIDELINE_REVIEW = ['proposed', 'accepted', 'dismissed'];
const GUIDELINE_ENFORCEMENTS = ['must', 'should', 'consider'];
const GUIDELINE_RULE_TYPES = ['max_discount_pct', 'max_premium_pct', 'value_share_pct', 'hourly_floor', 'roi_target', 'target_payback_months'];
const GUIDELINE_SOURCES = ['manual', 'ai_generated'];
const ASSIGNMENT_STATUSES = ['forecast', 'assigned', 'confirmed'];
const ASSIGNMENT_SOURCES = ['generated', 'manual'];
/** A contact's role on an opportunity (migration 0048). */
export const CONTACT_ROLES = ['decision_maker', 'economic_buyer', 'champion', 'influencer', 'technical_evaluator', 'end_user', 'other'];
/** Hours, to the tenth: "140h". */
const hoursOf = (v: unknown): string => (v === null || v === undefined ? '' : `${formatNumber(v)}h`);
const count = (v: unknown): string => formatNumber(v);
const optionalNumber = (v: unknown): string => (v === null || v === undefined ? '' : formatNumber(v));
/** A billing share, to the hundredth of a percent (33.33%). */
const share = (v: unknown): string => (v === null || v === undefined ? '' : `${formatNumber(v)}%`);

const DEFS: Record<ObjectKey, ObjectDef> = {
  leads: {
    key: 'leads',
    label: label('leads'),
    noun: 'leads',
    icon: <LeadIcon />,
    description: 'People who reached out through the site or the AI roadmap, before they become contacts.',
    ...schemaList({
      columns: [
        // First and last name, else the email, sorted by last name: the list returns the parts, not the record's name.
        { key: 'name', primary: true, width: 200, sortable: 'last_name', get: leadName },
        { key: 'company_name' },
        { key: 'status', nowrap: true, mobileHeader: true },
        { key: 'maturity_tier', nowrap: true },
        // Text that holds words as well as numbers: shown as stored (MOTHER-312), a number in tabular figures.
        { key: 'urgency_score', format: 'raw', figures: true, nowrap: true },
        { key: 'opportunity_name' },
        { key: 'created_at', format: formatDate },
      ],
      filters: ['first_name', 'last_name', 'email', 'company_name', 'status', 'maturity_tier', 'urgency_score', 'q9_timeline', 'source', 'created_at'],
      // The record page calls it Submitted; the list has always said Created.
      labels: { created_at: 'Created' },
    }),
    boardField: 'status',
  },
  accounts: {
    key: 'accounts',
    label: label('accounts'),
    noun: 'accounts',
    icon: <AccountIcon />,
    description: 'Companies Olytic sells to or works with.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 240 },
        { key: 'domain' },
        { key: 'status', nowrap: true, mobileHeader: true },
        { key: 'industry' },
        { key: 'icp_score' },
        { key: 'primary_contact_name' },
        { key: 'created_at', format: formatDate, hidden: true },
      ],
      filters: ['name', 'domain', 'status', 'industry', 'hq_state', 'employee_count', 'icp_score', 'primary_contact_name', 'created_at'],
    }),
    boardField: 'status',
  },
  contacts: {
    key: 'contacts',
    label: label('contacts'),
    noun: 'contacts',
    icon: <ContactIcon />,
    description: 'People at accounts.',
    ...schemaList({
      columns: [
        // First and last name, sorted by last name: the list returns the two, not the record's name.
        { key: 'name', primary: true, width: 200, sortable: 'last_name', get: (row) => `${str(row.first_name)} ${str(row.last_name)}`.trim() },
        { key: 'email' },
        { key: 'title' },
        { key: 'account_name' },
        { key: 'source', nowrap: true },
        { key: 'is_primary_contact', nowrap: true },
        { key: 'created_at', format: formatDate },
      ],
      filters: ['first_name', 'last_name', 'email', 'title', 'account_name', 'source', 'is_primary_contact', 'created_at'],
    }),
    boardField: 'source',
  },
  opportunities: {
    key: 'opportunities',
    label: label('opportunities'),
    noun: 'opportunities',
    icon: <OpportunityIcon />,
    description: 'Deals in the pipeline, from qualify to closed.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 240 },
        { key: 'account_name' },
        { key: 'stage', nowrap: true, mobileHeader: true },
        { key: 'amount' },
        { key: 'probability' },
        { key: 'close_date' },
        // Owners are names, not codes.
        { key: 'owner_name', format: 'raw', nowrap: true },
      ],
      filters: ['name', 'account_name', 'stage', 'amount', 'probability', 'close_date', 'owner_name', 'primary_quote_name', 'created_at'],
    }),
    boardField: 'stage',
    kanbanCardFields: ['account_name', 'amount', 'close_date'],
    kanbanSum: { key: 'amount', format: (total) => formatCurrency(total) },
  },
  audiences: {
    key: 'audiences',
    label: label('audiences'),
    noun: 'audiences',
    icon: <AudienceIcon />,
    description: 'Lists of contacts or accounts, kept by hand or by a rule.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 240 },
        { key: 'description', hideOnMobile: true },
        { key: 'entity_type', nowrap: true },
        { key: 'membership_type', nowrap: true, mobileHeader: true },
        { key: 'member_count' },
        // When it was created, without the time, as before.
        { key: 'created_at', format: formatDate },
      ],
      filters: ['name', 'description', 'entity_type', 'membership_type', 'member_count', 'created_at'],
    }),
    boardField: 'membership_type',
  },
  projects: {
    key: 'projects',
    label: label('projects'),
    noun: 'projects',
    icon: <ProjectIcon />,
    description: 'Client work that time is logged against.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 240 },
        { key: 'code', nowrap: true },
        { key: 'status', nowrap: true },
        { key: 'account_name' },
        // Owners are names, not codes.
        { key: 'owner_name', format: 'raw', nowrap: true },
        { key: 'created_at', format: formatDate },
      ],
      filters: ['name', 'code', 'status', 'account_name', 'owner_name', 'created_at'],
    }),
    boardField: 'owner_name',
  },
  'time-entries': {
    key: 'time-entries',
    label: label('time-entries'),
    noun: 'time entries',
    icon: <TimeEntryIcon />,
    description: 'Hours logged against projects.',
    columns: [
      col('entry_date', 'Date', formatDate, { ...date, primary: true }),
      col('account_name', 'Account', undefined, { link: lookup('accounts', 'account_id') }),
      col('project_name', 'Project', undefined, { link: lookup('projects', 'project_id') }),
      col('hours', 'Hours', (v) => formatNumber(v), figures),
      col('billable', 'Billable', undefined, date),
      col('notes', 'Notes', undefined, { hideOnMobile: true }),
      col('owner_name', 'Person', undefined, date),
    ],
    filterFields: [
      fdate('entry_date', 'Date'), ftext('account_name', 'Account'), ftext('project_name', 'Project'),
      fnum('hours', 'Hours'), fbool('billable', 'Billable'), ftext('notes', 'Notes'), fpick('owner_name', 'Person'),
    ],
    boardField: 'owner_name',
    kanbanCardFields: ['project_name', 'hours', 'billable'],
    kanbanSum: { key: 'hours', format: (total) => `${formatNumber(total)} h` },
    fields: [
      f('id', T.id), f('entry_date', T.date), f('account_id', T.lookup, 'accounts'), f('account_name', T.text),
      f('project_id', T.lookup, 'projects'), f('project_name', T.text),
      f('hours', T.number), f('billable', T.bool), f('notes', T.text), f('owner_name', T.text),
    ],
  },
  meetings: {
    key: 'meetings',
    label: label('meetings'),
    noun: 'meetings',
    icon: <MeetingIcon />,
    description: 'Client and internal meetings, with how they went.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 240 },
        { key: 'meeting_date' },
        // Every account and project linked to the meeting, comma-separated, linked to the first.
        { key: 'account_name' },
        { key: 'project_name' },
        { key: 'meeting_category', nowrap: true },
        { key: 'sentiment', nowrap: true },
        { key: 'client_health', nowrap: true, mobileHeader: true },
      ],
      filters: ['name', 'meeting_date', 'account_name', 'project_name', 'meeting_category', 'sentiment', 'client_health', 'status'],
      // The record page shows the name as Meeting Name (its title field) and its accounts and projects as
      // related lists, so the list labels these three itself, as it always has.
      labels: { name: 'Meeting Name', account_name: 'Account', project_name: 'Project' },
    }),
    boardField: 'client_health',
  },
  activities: {
    key: 'activities',
    label: label('activities'),
    noun: 'activities',
    icon: <ActivityIcon />,
    description: 'Emails, calls, messages and notes, in one timeline.',
    columns: [
      col('subject', 'Subject', undefined, { primary: true, width: 220 }),
      col('channel', 'Channel', humanize, { ...date, mobileHeader: true }),
      col('direction', 'Direction', humanize, date),
      col('occurred_at', 'When', formatDateTime, date),
      col('summary', 'Summary', undefined, { hideOnMobile: true }),
      col('owner_name', 'Owner', undefined, ownerCol),
    ],
    filterFields: [
      ftext('subject', 'Subject'), fpick('channel', 'Channel', ACTIVITY_CHANNELS),
      fpick('direction', 'Direction', ACTIVITY_DIRECTIONS), fdate('occurred_at', 'When'), ftext('summary', 'Summary'),
      fpick('owner_name', 'Owner'),
    ],
    boardField: 'channel',
    fields: [
      f('id', T.id), f('channel', T.picklist), f('direction', T.picklist), f('occurred_at', T.datetime),
      f('subject', T.text), f('summary', T.text), f('body', T.text), f('owner_id', T.lookup, 'people'), f('owner_name', T.text),
    ],
  },
  documents: {
    key: 'documents',
    label: label('documents'),
    noun: 'documents',
    icon: <DocumentIcon />,
    description: 'Files uploaded or synced into ops.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 240 },
        // A file type shows as it is written ("application/pdf"), not as a picklist value.
        { key: 'mime_type', format: 'raw', hideOnMobile: true },
        { key: 'byte_size', format: formatBytes },
        { key: 'source', nowrap: true },
        { key: 'uploaded_at' },
        // Owners are names, not codes.
        { key: 'owner_name', format: 'raw', nowrap: true },
      ],
      filters: ['name', 'mime_type', 'byte_size', 'source', 'uploaded_at', 'owner_name'],
      filterLabels: { byte_size: 'Size (Bytes)' },
    }),
    boardField: 'source',
  },
  products: {
    key: 'products',
    label: label('products'),
    noun: 'products',
    icon: <PartIcon />,
    description: 'What Olytic sells: consulting, Studio build work, Foundation licenses and add-ons. Drafts are not quoted until activated.',
    ...schemaList({
      columns: [
        { key: 'code', primary: true, nowrap: true, width: 110 },
        { key: 'name', width: 260 },
        // A group's name shows as it is written ("Add-ons"), not as a picklist value.
        { key: 'group_name', nowrap: true, format: 'raw' },
        { key: 'type', nowrap: true },
        { key: 'structure', nowrap: true },
        { key: 'status', nowrap: true, mobileHeader: true },
      ],
      filters: ['code', 'name', 'group_name', 'family', 'type', 'structure', 'is_component', 'status', 'size'],
    }),
    boardField: 'status',
    kanbanCardFields: ['name', 'group_name', 'type'],
  },
  'product-groups': {
    key: 'product-groups',
    label: label('product-groups'),
    noun: 'product groups',
    icon: <PartIcon />,
    description: 'How products are grouped within a family (Design, Identity, License…). A group sets its products\' family and revenue stream.',
    columns: [
      nameCol('Group'),
      col('family', 'Family', humanize, date),
      col('revenue_stream', 'Revenue Stream', humanize, date),
      col('products', 'Products', count, figures),
    ],
    filterFields: [ftext('name', 'Name'), fpick('family', 'Family', PRODUCT_FAMILIES), fpick('revenue_stream', 'Revenue Stream', REVENUE_STREAMS)],
    fields: [
      f('id', T.id), f('code', T.text), f('name', T.text), f('family', T.picklist), f('revenue_stream', T.picklist),
      f('description', T.text), f('products', T.number), f('sort_order', T.number),
    ],
  },
  prices: {
    key: 'prices',
    label: label('prices'),
    noun: 'prices',
    icon: <InvoiceIcon />,
    description: 'The ways each product is sold: a selling model, a scheme (flat, per hour, Price Grid or included) and an amount, one the default.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 220 },
        { key: 'product_name' },
        // A selling model and a Price Grid's name show as they are written ("One-time", "Acme-100 by Size"), not as
        // picklist values (MOTHER-313 criterion 8).
        { key: 'selling_model', format: 'raw', nowrap: true },
        { key: 'scheme', nowrap: true },
        { key: 'unit_price' },
        { key: 'price_grid_name', format: 'raw' },
        { key: 'is_default', nowrap: true },
      ],
      filters: ['name', 'product_name', 'selling_model', 'scheme', 'period', 'unit_price', 'price_grid_name', 'is_default', 'effective_from', 'effective_to'],
      // The list names it Price; its page is titled by it.
      labels: { name: 'Price' },
    }),
    boardField: 'scheme',
  },
  quotes: {
    key: 'quotes',
    label: label('quotes'),
    noun: 'quotes',
    icon: <ContractIcon />,
    description: 'Priced offers to accounts; options are sibling quotes on one opportunity.',
    columns: [
      col('quote_number', 'Quote', undefined, { primary: true, width: 140 }),
      col('name', 'Name'),
      col('account_name', 'Account', undefined, { link: lookup('accounts', 'account_id') }),
      col('opportunity_name', 'Opportunity', undefined, { link: lookup('opportunities', 'opportunity_id') }),
      col('status', 'Status', humanize, { ...date, mobileHeader: true }),
      col('one_time_net', 'One-Time', formatMoney, figures),
      col('monthly_net', 'Monthly', formatMoney, figures),
    ],
    filterFields: [
      ftext('quote_number', 'Quote'), ftext('name', 'Name'), ftext('account_name', 'Account'), ftext('opportunity_name', 'Opportunity'),
      fpick('kind', 'Type', QUOTE_KINDS), fpick('status', 'Status', QUOTE_STATUSES), fbool('is_primary', 'Primary'),
      fnum('one_time_net', 'One-Time'), fnum('monthly_net', 'Monthly'),
      fpick('owner_name', 'Owner'), fdate('created_at', 'Created'),
    ],
    boardField: 'status',
    kanbanCardFields: ['account_name', 'one_time_net', 'monthly_net'],
    kanbanSum: { key: 'one_time_net', format: (total) => formatCurrency(total) },
    fields: [
      f('id', T.id), f('quote_number', T.text), f('name', T.text), f('account_id', T.lookup, 'accounts'), f('account_name', T.text),
      f('opportunity_id', T.lookup, 'opportunities'), f('opportunity_name', T.text), f('kind', T.picklist),
      f('status', T.picklist), f('is_primary', T.bool), f('one_time_net', T.currency), f('monthly_net', T.currency),
      f('owner_id', T.lookup, 'people'), f('owner_name', T.text), f('created_at', T.datetime),
    ],
  },
  assets: {
    key: 'assets',
    label: label('assets'),
    noun: 'assets',
    icon: <EquipmentIcon />,
    description: 'What each account owns or subscribes to, at the price it pays.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 240 },
        // The account and app (project) link to their records; labels come from the record page (App).
        { key: 'account_name' },
        { key: 'project_name' },
        { key: 'billing', nowrap: true },
        { key: 'unit_price' },
        { key: 'start_date' },
        { key: 'end_date' },
      ],
      filters: ['name', 'account_name', 'project_name', 'product_code', 'billing', 'quantity', 'unit_price', 'start_date', 'end_date', 'active'],
    }),
    boardField: 'billing',
    kanbanCardFields: ['account_name', 'unit_price'],
  },
  revenue: {
    key: 'revenue',
    label: label('revenue'),
    noun: 'revenue schedule rows',
    icon: <ReportIcon />,
    description: "Each quote line's revenue and hours by month: the forecast from open opportunities' primary quotes, and what was won.",
    columns: [
      col('period_start', 'Month', formatMonth, { primary: true, nowrap: true, width: 120 }),
      col('quote_name', 'Quote', undefined, { link: lookup('quotes', 'quote_id'), nowrap: true }),
      col('product_name', 'Product', undefined, { link: lookup('products', 'product_id'), width: 220 }),
      col('opportunity_name', 'Opportunity', undefined, { link: lookup('opportunities', 'opportunity_id') }),
      col('amount', 'Amount', formatMoney, figures),
      col('weighted', 'Weighted', formatMoney, figures),
      col('hours', 'Hours', (v) => (v === null || v === undefined ? '' : formatNumber(v)), figures),
    ],
    filterFields: [
      fdate('period_start', 'Starts'), ftext('month', 'Month (YYYY-MM)'), ftext('quote_name', 'Quote'), ftext('line_name', 'Quote Line'), fpick('product_name', 'Product'), fpick('selling_model', 'Sold as'), fpick('billing', 'Billing', BILLINGS), ftext('opportunity_name', 'Opportunity'),
      fpick('account_name', 'Account'), fpick('stage', 'Stage', OPPORTUNITY_STAGES), fnum('amount', 'Amount'),
      fnum('weighted', 'Weighted'), fnum('hours', 'Hours'), fbool('is_primary', 'Primary Quote'), fbool('closed', 'Closed'), fbool('won', 'Won'),
    ],
    boardField: 'stage',
    kanbanCardFields: ['quote_name', 'product_name', 'amount'],
    kanbanSum: { key: 'amount', format: (total) => formatCurrency(total) },
    fields: [
      f('id', T.id), f('period_start', T.date), f('period_end', T.date), f('month', T.text), f('line_name', T.text),
      f('product_id', T.lookup, 'products'), f('product_name', T.text), f('selling_model', T.text), f('billing', T.picklist),
      f('quote_id', T.lookup, 'quotes'), f('quote_name', T.text), f('opportunity_id', T.lookup, 'opportunities'),
      f('opportunity_name', T.text), f('account_id', T.lookup, 'accounts'), f('account_name', T.text), f('stage', T.picklist),
      f('amount', T.currency), f('weighted', T.currency), f('hours', T.number), f('is_primary', T.bool), f('closed', T.bool), f('won', T.bool),
    ],
  },
  'product-options': {
    key: 'product-options',
    label: label('product-options'),
    noun: 'options',
    icon: <PartIcon />,
    description: "A bundle's options: the products a bundle (Studio Implementation) is configured from.",
    columns: [
      col('name', 'Option', undefined, { primary: true, nowrap: true, width: 180 }),
      col('bundle_product_name', 'Bundle', undefined, { link: lookup('products', 'bundle_product_id') }),
      col('option_product_name', 'Product', undefined, { link: lookup('products', 'option_product_id') }),
      col('option_group', 'Group', undefined, date),
      col('default_quantity', 'Default Qty', (v) => (v === null || v === undefined ? '' : formatNumber(v)), figures),
      col('required', 'Required', undefined, date),
      col('included', 'Included', undefined, date),
    ],
    filterFields: [
      ftext('name', 'Option'), fpick('bundle_product_name', 'Bundle'), ftext('option_product_name', 'Product'), fpick('option_group', 'Group'),
      fnum('default_quantity', 'Default Qty'), fbool('required', 'Required'), fbool('included', 'Included'),
    ],
    boardField: 'bundle_product_name',
    fields: [
      f('id', T.id), f('name', T.text), f('bundle_product_id', T.lookup, 'products'), f('bundle_product_name', T.text),
      f('option_product_id', T.lookup, 'products'), f('option_product_name', T.text), f('option_group', T.text),
      f('required', T.bool), f('default_quantity', T.number), f('included', T.bool),
    ],
  },
  'price-grids': {
    key: 'price-grids',
    label: label('price-grids'),
    noun: 'price grids',
    icon: <InvoiceIcon />,
    description: 'Fixed menus of prices: a fixed amount and hours per Company Size, or per Company Size and Quantity. A price points at one.',
    columns: [
      nameCol('Price Grid'),
      col('dimensions', 'Pricing', undefined, date),
      col('cells', 'Cells', (v) => formatNumber(v), figures),
      col('prices', 'Products Using It', (v) => formatNumber(v), figures),
      col('active', 'Active', undefined, date),
    ],
    filterFields: [ftext('name', 'Name'), fpick('prices_by', 'Pricing', PRICE_GRID_PRESETS), fbool('active', 'Active'), fdate('created_at', 'Created')],
    boardField: 'prices_by',
    fields: [
      f('id', T.id), f('name', T.text), f('prices_by', T.picklist), f('dimensions', T.text), f('cells', T.number), f('prices', T.number),
      f('active', T.bool), f('created_at', T.datetime),
    ],
  },
  'price-grid-cells': {
    key: 'price-grid-cells',
    label: label('price-grid-cells'),
    noun: 'cells',
    icon: <InvoiceIcon />,
    description: "A Price Grid's cells: for one Company Size (and quantity), a fixed amount and the hours it takes.",
    columns: [
      nameCol('Cell'),
      col('grid_name', 'Price Grid', undefined, { link: lookup('price-grids', 'grid_id') }),
      col('company_size', 'Company Size', undefined, date),
      col('quantity', 'Quantity', undefined, figures),
      col('amount', 'Amount', formatMoney, figures),
      col('hours', 'Hours', (v) => formatNumber(v), figures),
    ],
    filterFields: [fpick('grid_name', 'Price Grid'), fpick('company_size', 'Company Size'), fpick('quantity', 'Quantity'), fnum('amount', 'Amount'), fnum('hours', 'Hours')],
    boardField: 'grid_name',
    fields: [
      f('id', T.id), f('name', T.text), f('grid_id', T.lookup, 'price-grids'), f('grid_name', T.text), f('company_size', T.text),
      f('quantity', T.text), f('amount', T.currency), f('hours', T.number), f('label', T.text),
    ],
  },

  invoices: {
    key: 'invoices',
    label: label('invoices'),
    noun: 'invoices',
    icon: <InvoiceIcon />,
    description: 'One billing account on one invoice date, made by an invoice run. Draft until approved, then synced to QuickBooks.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, nowrap: true, width: 140 },
        { key: 'account_name' },
        { key: 'billing_account_name' },
        { key: 'invoice_date' },
        { key: 'due_date' },
        { key: 'status', nowrap: true, mobileHeader: true },
        { key: 'total' },
      ],
      filters: ['name', 'account_name', 'billing_account_name', 'invoice_date', 'due_date', 'status', 'total', 'balance', 'created_at'],
      // The list names its number Invoice and its dates Date and Due; its page, Invoice Number, Invoice Date and Due Date.
      labels: { name: 'Invoice', invoice_date: 'Date', due_date: 'Due' },
    }),
    boardField: 'status',
    kanbanCardFields: ['account_name', 'invoice_date', 'total'],
    kanbanSum: { key: 'total', format: (total) => formatMoney(total) },
  },
  'invoice-lines': {
    key: 'invoice-lines',
    label: label('invoice-lines'),
    noun: 'invoice lines',
    icon: <InvoiceIcon />,
    description: "One billing schedule row on an invoice, mapped to its product's QuickBooks item. Corrections are credit memos, never edits.",
    columns: [
      nameCol('Line'),
      col('invoice_name', 'Invoice', undefined, { link: lookup('invoices', 'invoice_id'), nowrap: true }),
      col('account_name', 'Account', undefined, { link: lookup('accounts', 'account_id') }),
      col('product_name', 'Product', undefined, { link: lookup('products', 'product_id') }),
      col('invoice_date', 'Date', formatDate, date),
      col('amount', 'Amount', formatMoney, figures),
    ],
    filterFields: [
      ftext('name', 'Line'), ftext('invoice_name', 'Invoice'), ftext('account_name', 'Account'), fpick('product_name', 'Product'),
      fdate('invoice_date', 'Date'), fpick('invoice_status', 'Invoice Status', INVOICE_STATUSES), fnum('amount', 'Amount'),
    ],
    boardField: 'invoice_status',
    kanbanCardFields: ['invoice_name', 'amount'],
    kanbanSum: { key: 'amount', format: (total) => formatMoney(total) },
    fields: [
      f('id', T.id), f('name', T.text), f('invoice_id', T.lookup, 'invoices'), f('invoice_name', T.text),
      f('account_id', T.lookup, 'accounts'), f('account_name', T.text), f('product_id', T.lookup, 'products'), f('product_name', T.text),
      f('billing_schedule_id', T.lookup, 'billing-schedules'), f('billing_schedule_name', T.text), f('invoice_date', T.date),
      f('invoice_status', T.picklist), f('amount', T.currency),
    ],
  },
  'invoice-runs': {
    key: 'invoice-runs',
    label: label('invoice-runs'),
    noun: 'invoice runs',
    icon: <QueueIcon />,
    description: 'One pass of invoice generation: every ready billing schedule row due by its through date, grouped onto invoices.',
    columns: [
      // The name is when it ran, so it sorts by that.
      { ...nameCol('Invoice Run'), sortable: 'run_at' },
      col('through_date', 'Through', formatDate, date),
      col('status', 'Status', humanize, { ...date, mobileHeader: true }),
      col('invoice_count', 'Invoices', count, figures),
      col('total', 'Total', formatMoney, figures),
      col('created_by_name', 'Run by'),
    ],
    filterFields: [
      fdate('run_at', 'Run'), fdate('through_date', 'Through'), fpick('status', 'Status', INVOICE_RUN_STATUSES),
      fnum('invoice_count', 'Invoices'), fpick('created_by_name', 'Run by'),
    ],
    boardField: 'status',
    kanbanCardFields: ['through_date', 'invoice_count', 'total'],
    fields: [
      f('id', T.id), f('name', T.text), f('run_at', T.datetime), f('through_date', T.date), f('status', T.picklist),
      f('invoice_count', T.number), f('total', T.currency), f('created_by_name', T.text),
    ],
  },
  'billing-schedules': {
    key: 'billing-schedules',
    label: label('billing-schedules'),
    noun: 'billing schedule rows',
    icon: <ScheduleIcon />,
    description: "When each commercial line is invoiced and how much: on the quote line while quoted, on the order line once sold.",
    columns: [
      nameCol('Billing Row'),
      col('account_name', 'Account', undefined, { link: lookup('accounts', 'account_id') }),
      col('quote_name', 'Quote', undefined, { link: lookup('quotes', 'quote_id'), nowrap: true }),
      col('planned_date', 'Invoice Date', formatDate, date),
      col('amount', 'Amount', formatMoney, figures),
      col('status', 'Status', humanize, { ...date, mobileHeader: true }),
      col('source', 'On', humanize, date),
    ],
    filterFields: [
      ftext('name', 'Billing Row'), fpick('source', 'On', BILLING_SCHEDULE_SOURCES), ftext('quote_name', 'Quote'),
      fpick('account_name', 'Account'), ftext('opportunity_name', 'Opportunity'), ftext('billing_account_name', 'Billing Account'),
      fdate('planned_date', 'Invoice Date'), fnum('percent', 'Percent'), fnum('amount', 'Amount'),
      fpick('status', 'Status', BILLING_SCHEDULE_STATUSES),
    ],
    boardField: 'status',
    kanbanCardFields: ['account_name', 'planned_date', 'amount'],
    kanbanSum: { key: 'amount', format: (total) => formatMoney(total) },
    fields: [
      f('id', T.id), f('name', T.text), f('label', T.text), f('source', T.picklist), f('quote_id', T.lookup, 'quotes'),
      f('quote_name', T.text), f('account_id', T.lookup, 'accounts'), f('account_name', T.text),
      f('opportunity_id', T.lookup, 'opportunities'), f('opportunity_name', T.text),
      f('billing_account_id', T.lookup, 'billing-accounts'), f('billing_account_name', T.text), f('planned_date', T.date),
      f('percent', T.percent), f('amount', T.currency), f('status', T.picklist),
    ],
  },
  'billing-accounts': {
    key: 'billing-accounts',
    label: label('billing-accounts'),
    noun: 'billing accounts',
    icon: <AccountIcon />,
    description: "Who an invoice goes to: the bill-to. Each account has a default one; a second one splits its invoices.",
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 240 },
        { key: 'account_name' },
        { key: 'is_default', nowrap: true },
        { key: 'invoice_contact_name' },
        { key: 'invoice_email' },
        { key: 'payment_terms', nowrap: true },
      ],
      filters: ['name', 'account_name', 'is_default', 'invoice_contact_name', 'invoice_email', 'payment_terms', 'created_at'],
      // The list names it Billing Account and its terms Terms; its page, Name and Payment Terms.
      labels: { name: 'Billing Account', payment_terms: 'Terms' },
    }),
    boardField: 'payment_terms',
    kanbanCardFields: ['account_name', 'invoice_email'],
  },
  'billing-policies': {
    key: 'billing-policies',
    label: label('billing-policies'),
    noun: 'billing policies',
    icon: <DocumentIcon />,
    description: "A family of products' billing options, shared by every product that points to it; one treatment is the default.",
    columns: [
      nameCol('Billing Policy'),
      col('description', 'Description', undefined, { width: 320 }),
      col('treatments', 'Treatments', count, figures),
      col('products', 'Products', count, figures),
      col('active', 'Active', undefined, date),
    ],
    filterFields: [ftext('name', 'Name'), ftext('description', 'Description'), fbool('active', 'Active'), fdate('created_at', 'Created')],
    fields: [
      f('id', T.id), f('name', T.text), f('description', T.text), f('active', T.bool), f('treatments', T.number),
      f('products', T.number), f('created_at', T.datetime),
    ],
  },
  'billing-treatments': {
    key: 'billing-treatments',
    label: label('billing-treatments'),
    noun: 'billing treatments',
    icon: <ScheduleIcon />,
    description: 'One way to bill under a policy: milestones, installments, recurring in advance or in arrears, on completion or on purchase.',
    columns: [
      nameCol('Billing Treatment'),
      col('billing_policy_name', 'Billing Policy', undefined, { link: lookup('billing-policies', 'billing_policy_id') }),
      col('method', 'Method', humanize, date),
      col('billing_timing', 'Timing', humanize, date),
      col('billing_frequency', 'Frequency', humanize, date),
      col('is_default', 'Default', undefined, date),
      col('active', 'Active', undefined, date),
    ],
    filterFields: [
      ftext('name', 'Name'), fpick('billing_policy_name', 'Billing Policy'), fpick('method', 'Method', BILLING_METHODS),
      fpick('billing_timing', 'Timing', BILLING_TIMINGS), fpick('billing_frequency', 'Frequency', BILLING_FREQUENCIES),
      fbool('is_default', 'Default'), fbool('active', 'Active'),
    ],
    boardField: 'method',
    kanbanCardFields: ['billing_policy_name', 'billing_frequency'],
    fields: [
      f('id', T.id), f('name', T.text), f('billing_policy_id', T.lookup, 'billing-policies'), f('billing_policy_name', T.text),
      f('method', T.picklist), f('billing_timing', T.picklist), f('billing_frequency', T.picklist), f('is_default', T.bool),
      f('active', T.bool), f('sort_order', T.number),
    ],
  },
  'billing-treatment-items': {
    key: 'billing-treatment-items',
    label: label('billing-treatment-items'),
    noun: 'treatment items',
    icon: <ScheduleIcon />,
    description: "One milestone or installment in a treatment (Kickoff deposit 25%). A line's billing schedule copies these.",
    columns: [
      nameCol('Item'),
      col('billing_treatment_name', 'Billing Treatment', undefined, { link: lookup('billing-treatments', 'billing_treatment_id') }),
      col('sequence', 'Order', count, figures),
      col('percent', 'Percent', share, figures),
      col('trigger', 'Billed', humanize, date),
      col('month_offset', 'Months After Kickoff', optionalNumber, figures),
    ],
    filterFields: [
      ftext('name', 'Item'), fpick('billing_treatment_name', 'Billing Treatment'), fnum('sequence', 'Order'),
      fnum('percent', 'Percent'), fpick('trigger', 'Billed', BILLING_TRIGGERS), fnum('month_offset', 'Months After Kickoff'),
    ],
    boardField: 'billing_treatment_name',
    kanbanCardFields: ['percent', 'trigger'],
    fields: [
      f('id', T.id), f('name', T.text), f('billing_treatment_id', T.lookup, 'billing-treatments'), f('billing_treatment_name', T.text),
      f('sequence', T.number), f('percent', T.percent), f('trigger', T.picklist), f('month_offset', T.number),
    ],
  },
  contracts: {
    key: 'contracts',
    label: label('contracts'),
    noun: 'contracts',
    icon: <ContractIcon />,
    description: 'The agreement an accepted order starts. Later orders that add scope or products reference it, and bill to its dates.',
    ...schemaList({
      columns: [
        { key: 'name', primary: true, width: 240 },
        { key: 'account_name' },
        { key: 'opportunity_name' },
        { key: 'start_date' },
        { key: 'end_date' },
        { key: 'status', nowrap: true, mobileHeader: true },
      ],
      filters: ['name', 'account_name', 'opportunity_name', 'start_date', 'end_date', 'status', 'created_at'],
      // The list names it Contract; its page, Contract Name.
      labels: { name: 'Contract' },
    }),
    boardField: 'status',
    kanbanCardFields: ['account_name', 'start_date', 'end_date'],
  },

  // Product setup (migrations 0032, 0034).
  guidelines: {
    key: 'guidelines',
    label: label('guidelines'),
    noun: 'guidelines',
    icon: <DocumentIcon />,
    description: 'How Olytic prices and delivers, as statements the agents read: global, for products, or for one opportunity; must, should or consider.',
    columns: [
      nameCol('Guideline'),
      col('topic', 'Topic', humanize, date),
      col('scope', 'Scope', humanize, date),
      col('enforcement', 'Enforcement', humanize, date),
      col('weight', 'Weight', count, figures),
      col('opportunity_name', 'Opportunity', undefined, { link: lookup('opportunities', 'opportunity_id') }),
      col('active', 'Active', undefined, date),
    ],
    filterFields: [
      ftext('name', 'Name'), fpick('topic', 'Topic', GUIDELINE_TOPICS), fpick('scope', 'Scope', GUIDELINE_SCOPES),
      fpick('enforcement', 'Enforcement', GUIDELINE_ENFORCEMENTS), fnum('weight', 'Weight'), fpick('rule_type', 'Rule', GUIDELINE_RULE_TYPES),
      fnum('rule_value', 'Rule Value'), ftext('opportunity_name', 'Opportunity'), fpick('source', 'Source', GUIDELINE_SOURCES),
      fpick('review_status', 'Review', GUIDELINE_REVIEW), fbool('active', 'Active'), fdate('created_at', 'Created'),
    ],
    boardField: 'enforcement',
    kanbanCardFields: ['topic', 'scope', 'weight'],
    fields: [
      f('id', T.id), f('name', T.text), f('topic', T.picklist), f('scope', T.picklist), f('enforcement', T.picklist),
      f('weight', T.number), f('rule_type', T.picklist), f('rule_value', T.number), f('opportunity_id', T.lookup, 'opportunities'),
      f('opportunity_name', T.text), f('products', T.number), f('source', T.picklist), f('review_status', T.picklist), f('active', T.bool), f('created_at', T.datetime),
    ],
  },
  'resource-roles': {
    key: 'resource-roles',
    label: label('resource-roles'),
    noun: 'delivery roles',
    icon: <TechnicianIcon />,
    description: "The kinds of delivery work (Project Lead, Engineer) a product's hours are split across.",
    columns: [
      nameCol('Delivery Role'),
      col('description', 'Description', undefined, { width: 320 }),
      col('products', 'Products', count, figures),
      col('sort_order', 'Order', count, figures),
      col('active', 'Active', undefined, date),
    ],
    filterFields: [ftext('name', 'Name'), ftext('description', 'Description'), fnum('sort_order', 'Order'), fbool('active', 'Active')],
    fields: [
      f('id', T.id), f('name', T.text), f('description', T.text), f('sort_order', T.number), f('products', T.number), f('active', T.bool),
    ],
  },
  'product-resource-roles': {
    key: 'product-resource-roles',
    label: label('product-resource-roles'),
    noun: 'role shares',
    icon: <TechnicianIcon />,
    description: "Which roles staff a product and each one's share of its hours; a service product's shares total 100%.",
    columns: [
      nameCol('Role Share'),
      col('product_name', 'Product', undefined, { link: lookup('products', 'product_id') }),
      col('resource_role_name', 'Role', undefined, { link: lookup('resource-roles', 'resource_role_id') }),
      col('share_pct', 'Share of Hours', share, figures),
    ],
    filterFields: [ftext('name', 'Role Share'), ftext('product_name', 'Product'), fpick('resource_role_name', 'Role'), fnum('share_pct', 'Share of Hours')],
    boardField: 'resource_role_name',
    kanbanCardFields: ['product_name', 'share_pct'],
    fields: [
      f('id', T.id), f('name', T.text), f('product_id', T.lookup, 'products'), f('product_name', T.text),
      f('resource_role_id', T.lookup, 'resource-roles'), f('resource_role_name', T.text), f('share_pct', T.percent),
    ],
  },

  // Resourcing (migration 0034).
  people: {
    key: 'people',
    label: label('people'),
    noun: 'people',
    icon: <ContactIcon />,
    description: 'The people who use Mothership and do the delivery work: their usual monthly hours and the roles they fill.',
    columns: [
      nameCol('Name'),
      col('email', 'Email'),
      col('primary_role_name', 'Primary Role', undefined, date),
      col('roles', 'Roles', count, figures),
      col('monthly_capacity_hours', 'Usual Monthly Hours', hoursOf, figures),
    ],
    filterFields: [ftext('name', 'Name'), ftext('email', 'Email'), fnum('monthly_capacity_hours', 'Usual Monthly Hours'), fdate('created_at', 'Created')],
    fields: [
      f('id', T.id), f('name', T.text), f('email', T.email), f('monthly_capacity_hours', T.number), f('primary_role_name', T.text),
      f('roles', T.number), f('created_at', T.datetime),
    ],
  },
  'user-resource-roles': {
    key: 'user-resource-roles',
    label: label('user-resource-roles'),
    noun: 'person roles',
    icon: <TechnicianIcon />,
    description: 'The roles a person can fill. Their primary role groups them on the Resourcing page.',
    columns: [
      nameCol('Person Role'),
      col('app_user_name', 'Person', undefined, { link: lookup('people', 'app_user_id') }),
      col('resource_role_name', 'Role', undefined, { link: lookup('resource-roles', 'resource_role_id') }),
      col('is_primary', 'Primary', undefined, date),
    ],
    filterFields: [ftext('name', 'Person Role'), fpick('app_user_name', 'Person'), fpick('resource_role_name', 'Role'), fbool('is_primary', 'Primary')],
    boardField: 'resource_role_name',
    kanbanCardFields: ['app_user_name', 'is_primary'],
    fields: [
      f('id', T.id), f('name', T.text), f('app_user_id', T.lookup, 'people'), f('app_user_name', T.text),
      f('resource_role_id', T.lookup, 'resource-roles'), f('resource_role_name', T.text), f('is_primary', T.bool),
    ],
  },
  'resource-capacity': {
    key: 'resource-capacity',
    label: label('resource-capacity'),
    noun: 'capacity months',
    icon: <TimeEntryIcon />,
    description: "A month a person's capacity differs from their usual hours: holidays, part-time months, a new starter's ramp.",
    columns: [
      nameCol('Capacity Month'),
      col('app_user_name', 'Person', undefined, { link: lookup('people', 'app_user_id') }),
      col('month', 'Month', formatMonth, date),
      col('hours', 'Hours', hoursOf, figures),
      col('usual_hours', 'Usual Hours', hoursOf, figures),
      col('note', 'Note', undefined, { width: 240 }),
    ],
    filterFields: [ftext('name', 'Capacity Month'), fpick('app_user_name', 'Person'), fdate('month', 'Month'), fnum('hours', 'Hours'), ftext('note', 'Note')],
    boardField: 'app_user_name',
    kanbanCardFields: ['month', 'hours', 'note'],
    fields: [
      f('id', T.id), f('name', T.text), f('app_user_id', T.lookup, 'people'), f('app_user_name', T.text), f('month', T.date),
      f('hours', T.number), f('usual_hours', T.number), f('note', T.text), f('created_at', T.datetime),
    ],
  },
  'resource-assignments': {
    key: 'resource-assignments',
    label: label('resource-assignments'),
    noun: 'resource assignments',
    icon: <ScheduleIcon />,
    description: "Hours of a role on a deal or project in a month, and who fills them: forecast (nobody named), assigned (named on an open deal) or confirmed (won work).",
    columns: [
      nameCol('Assignment'),
      col('account_name', 'Account', undefined, { link: lookup('accounts', 'account_id') }),
      col('resource_role_name', 'Role', undefined, { link: lookup('resource-roles', 'resource_role_id') }),
      col('app_user_name', 'Person', undefined, { link: lookup('people', 'app_user_id') }),
      col('month', 'Month', formatMonth, date),
      col('hours', 'Hours', hoursOf, figures),
      col('status', 'Status', humanize, { ...date, mobileHeader: true }),
    ],
    filterFields: [
      ftext('name', 'Assignment'), ftext('account_name', 'Account'), ftext('project_name', 'Project'), ftext('opportunity_name', 'Opportunity'),
      fpick('resource_role_name', 'Role'), fpick('app_user_name', 'Person'), fdate('month', 'Month'), fnum('hours', 'Hours'),
      fpick('status', 'Status', ASSIGNMENT_STATUSES), fpick('source', 'Source', ASSIGNMENT_SOURCES), fdate('edited_at', 'Edited'),
    ],
    boardField: 'status',
    kanbanCardFields: ['app_user_name', 'month', 'hours'],
    kanbanSum: { key: 'hours', format: (total) => `${formatNumber(total)}h` },
    fields: [
      f('id', T.id), f('name', T.text), f('account_id', T.lookup, 'accounts'), f('account_name', T.text),
      f('project_id', T.lookup, 'projects'), f('project_name', T.text), f('opportunity_id', T.lookup, 'opportunities'),
      f('opportunity_name', T.text), f('resource_role_id', T.lookup, 'resource-roles'), f('resource_role_name', T.text),
      f('app_user_id', T.lookup, 'people'), f('app_user_name', T.text), f('month', T.date), f('hours', T.number),
      f('status', T.picklist), f('source', T.picklist), f('edited_at', T.datetime),
    ],
  },
  'opportunity-contacts': {
    key: 'opportunity-contacts',
    label: label('opportunity-contacts'),
    noun: 'contact roles',
    icon: <ContactIcon />,
    description: 'The contacts on each deal, in what role, and who is primary.',
    columns: [
      nameCol('Contact Role'),
      col('contact_name', 'Contact', undefined, { link: lookup('contacts', 'contact_id') }),
      col('opportunity_name', 'Opportunity', undefined, { link: lookup('opportunities', 'opportunity_id') }),
      col('role', 'Role', humanize, { ...date, mobileHeader: true }),
      col('is_primary', 'Primary', undefined, date),
      col('created_at', 'Added', formatDate, date),
    ],
    filterFields: [
      ftext('name', 'Contact Role'), ftext('contact_name', 'Contact'), ftext('opportunity_name', 'Opportunity'),
      fpick('role', 'Role', CONTACT_ROLES), fbool('is_primary', 'Primary'), fdate('created_at', 'Added'),
    ],
    boardField: 'role',
    kanbanCardFields: ['contact_name', 'opportunity_name', 'is_primary'],
    fields: [
      f('id', T.id), f('name', T.text), f('contact_id', T.lookup, 'contacts'), f('contact_name', T.text),
      f('opportunity_id', T.lookup, 'opportunities'), f('opportunity_name', T.text), f('role', T.picklist),
      f('is_primary', T.bool), f('created_at', T.datetime),
    ],
  },
  'deal-retros': {
    key: 'deal-retros',
    label: label('deal-retros'),
    noun: 'Deal Retros',
    icon: <OpportunityIcon />,
    description: 'Why each deal was won or lost, in the words of the people who worked it.',
    columns: [
      nameCol('Deal Retro'),
      col('opportunity_name', 'Opportunity', undefined, { link: lookup('opportunities', 'opportunity_id') }),
      col('stage', 'Stage', humanize, { ...date, mobileHeader: true }),
      col('notes', 'Notes'),
      col('created_by_name', 'Created by'),
      col('created_at', 'Created', formatDateTime, date),
    ],
    filterFields: [
      ftext('name', 'Deal Retro'), ftext('opportunity_name', 'Opportunity'), fpick('stage', 'Stage', OPPORTUNITY_STAGES),
      ftext('notes', 'Notes'), fpick('created_by_name', 'Created by'), fdate('created_at', 'Created'), fdate('updated_at', 'Last Modified'),
    ],
    boardField: 'stage',
    kanbanCardFields: ['opportunity_name', 'created_by_name', 'created_at'],
    fields: [
      f('id', T.id), f('name', T.text), f('opportunity_id', T.lookup, 'opportunities'), f('opportunity_name', T.text),
      f('stage', T.picklist), f('notes', T.text), f('created_by_name', T.text), f('created_at', T.datetime), f('updated_at', T.datetime),
    ],
  },
  // Chats with the website's agents (0062). Lists carry the opening message, never the transcript.
  'agent-conversations': {
    key: 'agent-conversations',
    label: label('agent-conversations'),
    noun: 'Agent Conversations',
    icon: <AssistantIcon />,
    description: 'Chats with Ollie, the agent on olytic.com, and the leads they turned into.',
    columns: [
      nameCol('Conversation'),
      col('opening_message', 'Opening Message'),
      col('page_origin', 'Page'),
      col('message_count', 'Messages', count, figures),
      col('outcome', 'Outcome', humanize, { ...date, mobileHeader: true }),
      col('lead_name', 'Lead', undefined, { link: lookup('leads', 'lead_id') }),
      col('created_at', 'Started', formatDateTime, date),
    ],
    filterFields: [
      ftext('name', 'Conversation'), ftext('opening_message', 'Opening Message'), fpick('page_origin', 'Page'),
      fnum('message_count', 'Messages'), fpick('outcome', 'Outcome', AGENT_CONVERSATION_OUTCOMES), ftext('visitor_email', 'Visitor Email'),
      ftext('lead_name', 'Lead'), fpick('agent_type', 'Agent'), fdate('created_at', 'Started'), fdate('updated_at', 'Last Message'),
    ],
    boardField: 'outcome',
    kanbanCardFields: ['opening_message', 'page_origin', 'created_at'],
    fields: [
      f('id', T.id), f('name', T.text), f('agent_type', T.picklist), f('page_origin', T.picklist), f('opening_message', T.text),
      f('message_count', T.number), f('outcome', T.picklist), f('visitor_email', T.email), f('lead_id', T.lookup, 'leads'),
      f('lead_name', T.text), f('created_at', T.datetime), f('updated_at', T.datetime),
    ],
  },
  // The research library (0095): what Olytic cites, the findings taken from it and its charts. People add and edit them; figures come only from intake (writes.mjs).
  'research-sources': {
    key: 'research-sources',
    label: label('research-sources'),
    noun: 'research sources',
    icon: <DocumentIcon />,
    description: 'The papers, reports and articles Olytic cites, with their quality grade.',
    columns: [
      nameCol('Title'),
      col('publisher', 'Publisher'),
      col('published_on', 'Published', formatDate, date),
      col('quality_grade', 'Quality Grade', undefined, { ...date, mobileHeader: true }),
      col('finding_count', 'Findings', count, figures),
      col('tags', 'Tags'),
    ],
    filterFields: [
      ftext('name', 'Title'), fpick('publisher', 'Publisher'), fdate('published_on', 'Published'),
      fpick('quality_grade', 'Quality Grade', RESEARCH_GRADES), ftext('tags', 'Tags'), ftext('url', 'URL'), fdate('created_at', 'Created'),
    ],
    boardField: 'quality_grade',
    kanbanCardFields: ['publisher', 'published_on', 'finding_count'],
    fields: [
      f('id', T.id), f('name', T.text), f('publisher', T.text), f('published_on', T.date), f('quality_grade', T.picklist),
      f('finding_count', T.number), f('tags', T.text), f('url', T.text), f('created_at', T.datetime),
    ],
  },
  'research-findings': {
    key: 'research-findings',
    label: label('research-findings'),
    noun: 'research findings',
    icon: <ReportIcon />,
    description: 'The stats and quotes taken from research sources: what anything Olytic publishes may cite.',
    columns: [
      { ...nameCol('Statement'), width: 360 },
      col('source_name', 'Source', undefined, { link: lookup('research-sources', 'source_id') }),
      col('page_number', 'Page', count, figures),
      col('quote_verified', 'Quote Verified', undefined, date),
      col('figure_name', 'Figure', undefined, { link: lookup('research-figures', 'figure_id') }),
      col('tags', 'Tags'),
    ],
    filterFields: [
      ftext('name', 'Statement'), fpick('source_name', 'Source'), fnum('page_number', 'Page'),
      fbool('quote_verified', 'Quote Verified'), ftext('figure_name', 'Figure'), ftext('tags', 'Tags'), fdate('created_at', 'Created'),
    ],
    fields: [
      f('id', T.id), f('name', T.text), f('source_id', T.lookup, 'research-sources'), f('source_name', T.text),
      f('page_number', T.number), f('quote_verified', T.bool), f('figure_id', T.lookup, 'research-figures'), f('figure_name', T.text),
      f('tags', T.text), f('created_at', T.datetime),
    ],
  },
  'research-figures': {
    key: 'research-figures',
    label: label('research-figures'),
    noun: 'research figures',
    icon: <DashboardIcon />,
    description: 'The charts and infographics inside research sources.',
    columns: [
      nameCol('Title'),
      col('source_name', 'Source', undefined, { link: lookup('research-sources', 'source_id') }),
      col('page_number', 'Page', count, figures),
      col('description', 'Description'),
    ],
    filterFields: [
      ftext('name', 'Title'), fpick('source_name', 'Source'), fnum('page_number', 'Page'), ftext('description', 'Description'),
      fdate('created_at', 'Created'),
    ],
    fields: [
      f('id', T.id), f('name', T.text), f('source_id', T.lookup, 'research-sources'), f('source_name', T.text),
      f('page_number', T.number), f('description', T.text), f('created_at', T.datetime),
    ],
  },
  // Deal documents (0105): the blueprints the quote builder renders from, and what it produced.
  'deliverable-types': {
    key: 'deliverable-types',
    label: label('deliverable-types'),
    noun: 'deliverable types',
    icon: <DocumentIcon />,
    description: 'The deal documents a quote renders into: who each is for, its job, its spec and template, and the rules it follows.',
    columns: [
      nameCol('Deliverable Type'),
      col('code', 'Code'),
      col('renders_from', 'Renders From', humanize, date),
      col('audience', 'Audience'),
      col('deliverable_count', 'Deliverables', count, figures),
      col('exemplar_count', 'Exemplars', count, figures),
      col('active', 'Active', undefined, date),
    ],
    filterFields: [
      ftext('name', 'Name'), ftext('code', 'Code'), fpick('renders_from', 'Renders From', DELIVERABLE_SOURCES), ftext('audience', 'Audience'), fbool('signed', 'Signed'),
      fbool('active', 'Active'), ftext('research_tags', 'Research Tags'), fdate('created_at', 'Created'),
    ],
    fields: [
      f('id', T.id), f('name', T.text), f('code', T.text), f('renders_from', T.picklist), f('audience', T.text), f('signed', T.bool), f('sort_order', T.number),
      f('active', T.bool), f('deliverable_count', T.number), f('exemplar_count', T.number), f('research_tags', T.text),
      f('created_at', T.datetime), f('updated_at', T.datetime),
    ],
  },
  deliverables: {
    key: 'deliverables',
    label: label('deliverables'),
    noun: 'deliverables',
    icon: <ReportIcon />,
    description: 'The deal documents produced from quotes: proposals, memos, work orders and the rest, with the exemplars the quote builder imitates.',
    columns: [
      nameCol('Deliverable'),
      col('deliverable_type_name', 'Type', undefined, { link: lookup('deliverable-types', 'deliverable_type_id') }),
      col('quote_name', 'Quote', undefined, { link: lookup('quotes', 'quote_id') }),
      col('project_name', 'Project', undefined, { link: lookup('projects', 'project_id') }),
      col('status', 'Status', humanize, date),
      col('is_exemplar', 'Exemplar', undefined, date),
      col('updated_at', 'Last Modified', formatDateTime, date),
    ],
    filterFields: [
      ftext('name', 'Name'), fpick('deliverable_type_name', 'Type'), ftext('quote_name', 'Quote'), ftext('opportunity_name', 'Opportunity'),
      ftext('project_name', 'Project'), fnum('version', 'Version'), fpick('status', 'Status', DELIVERABLE_STATUSES), fbool('is_exemplar', 'Exemplar'),
      fpick('owner_name', 'Owner'), fdate('updated_at', 'Last Modified'),
    ],
    boardField: 'status',
    kanbanCardFields: ['deliverable_type_name', 'quote_name', 'project_name'],
    fields: [
      f('id', T.id), f('name', T.text), f('deliverable_type_id', T.lookup, 'deliverable-types'), f('deliverable_type_name', T.text),
      f('quote_id', T.lookup, 'quotes'), f('quote_name', T.text), f('opportunity_id', T.lookup, 'opportunities'),
      f('opportunity_name', T.text), f('project_id', T.lookup, 'projects'), f('project_name', T.text), f('version', T.number), f('status', T.picklist), f('is_exemplar', T.bool),
      f('artifact_url', T.text), f('owner_id', T.lookup, 'people'), f('owner_name', T.text), f('created_at', T.datetime), f('updated_at', T.datetime),
    ],
  },
  // Guides (0118): long-form instructions people and agents work from, such as the outreach voice guide and playbook.
  guides: {
    key: 'guides',
    label: label('guides'),
    noun: 'guides',
    icon: <DocumentIcon />,
    description: 'The long-form instructions people and agents work from, such as the outreach voice guide and playbook, each one versioned document.',
    columns: [
      nameCol('Guide'),
      col('code', 'Code'),
      col('domain', 'Domain'),
      col('version', 'Version', formatDate, date),
      col('status', 'Status', humanize, date),
      col('updated_at', 'Last Modified', formatDateTime, date),
    ],
    filterFields: [
      ftext('name', 'Name'), ftext('code', 'Code'), fpick('domain', 'Domain'), fpick('status', 'Status', GUIDE_STATUSES), fdate('version', 'Version'),
      fdate('updated_at', 'Last Modified'),
    ],
    fields: [
      f('id', T.id), f('name', T.text), f('code', T.text), f('domain', T.text), f('version', T.date), f('status', T.picklist), f('sort_order', T.number),
      f('created_at', T.datetime), f('updated_at', T.datetime),
    ],
  },
};

/**
 * Every object's definition, with its name column linked to the record page:
 * the href is the record's URL, and the list's row click (ObjectListPage)
 * routes a plain click client-side.
 */
export const OBJECT_DEFS: Record<ObjectKey, ObjectDef> = Object.fromEntries(
  Object.entries(DEFS).map(([key, def]) => [
    key,
    {
      ...def,
      columns: def.columns.map((c) =>
        c.primary ? { ...c, href: (row: ObjectRow) => recordPath(def.key, row.id) } : c,
      ),
    },
  ]),
) as Record<ObjectKey, ObjectDef>;
